import { useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import emblem from "@/assets/golden-wine-emblem.png";

export default function Preloader({ onDone, duration = 1700 }) {
  const { t } = useTranslation();
  useEffect(() => {
    const t = setTimeout(() => onDone?.(), duration);
    return () => clearTimeout(t);
  }, [onDone, duration]);

  return (
    <motion.div
      data-testid="preloader"
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-[#05070A] overflow-hidden"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.6, ease: "easeInOut" } }}
    >
      {/* Ambient gold halo */}
      <motion.div
        className="absolute w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(245,203,92,0.16) 0%, rgba(5,7,10,0) 65%)" }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: [0, 1, 0.7], scale: [0.6, 1.05, 1] }}
        transition={{ duration: 1.6, ease: "easeInOut" }}
      />

      {/* Emblem with entrance + soft glow pulse */}
      <motion.div
        className="relative"
        initial={{ opacity: 0, scale: 0.82, filter: "blur(6px)" }}
        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.img
          src={emblem}
          alt="Láser Vintage"
          className="w-28 h-28 sm:w-32 sm:h-32 object-contain"
          animate={{
            filter: [
              "drop-shadow(0 0 10px rgba(245,203,92,0.25))",
              "drop-shadow(0 0 26px rgba(245,203,92,0.55))",
              "drop-shadow(0 0 10px rgba(245,203,92,0.25))",
            ],
          }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      {/* Wordmark */}
      <motion.div
        className="mt-6 text-center"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.35 }}
      >
        <div className="font-display text-white text-sm sm:text-base tracking-[0.34em]">LÁSER VINTAGE</div>
        <div className="mt-1.5 text-[8px] sm:text-[9px] tracking-[0.38em] text-[#F5CB5C]/80">{t("navbar.brandSub")}</div>
      </motion.div>

      {/* Gold progress line */}
      <div className="mt-7 h-[2px] w-40 sm:w-52 overflow-hidden rounded-full bg-white/10">
        <motion.div
          className="h-full w-full origin-left rounded-full bg-gradient-to-r from-[#FAE19C] via-[#F5CB5C] to-[#C69222]"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: (duration - 250) / 1000, ease: "easeInOut" }}
        />
      </div>
    </motion.div>
  );
}
